import { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useEmployeeProfileListQuery } from "../api/queries";
import { EmployeeProfileFilter, type EmployeeProfileSearchParams } from "../ui/EmployeeProfileFilter";
import { EmployeeProfileTable } from "../ui/EmployeeProfileTable";
import type { EmployeeProfileListItem } from "@/modules/hr/employee-profile/domain/types";

export function EmployeeProfileListPage() {
  const nav = useNavigate({ from: "/app/hr/employees" });
  const search = useSearch({
    from: "/app/hr/employees",
  }) as EmployeeProfileSearchParams;

  const query = useMemo(
    () => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      search: search.search,
      branch: search.branch,
      status: search.status as EmployeeProfileListItem["status"] | undefined,
      departmentId: search.departmentId,
    }),
    [search],
  );

  const listQuery = useEmployeeProfileListQuery(query);
  const rows = listQuery.data?.data ?? [];

  function handleSearchChange(params: Partial<EmployeeProfileSearchParams>) {
    nav({
      search: {
        offset: params.offset ?? search.offset ?? 0,
        limit: params.limit ?? search.limit ?? 20,
        search: params.search !== undefined ? params.search : search.search,
        branch: params.branch !== undefined ? params.branch : search.branch,
        status: params.status !== undefined ? params.status : search.status,
        departmentId: params.departmentId !== undefined ? params.departmentId : search.departmentId,
      },
    });
  }

  function handlePage(offset: number, limit: number) {
    nav({
      search: {
        offset,
        limit,
        search: search.search,
        branch: search.branch,
        status: search.status,
        departmentId: search.departmentId,
      },
    });
  }

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="font-semibold text-2xl">ລາຍຊື່ພະນັກງານ</h1>
        </div>

        <div className="flex flex-col rounded-xl border bg-card">
          <EmployeeProfileFilter
            searchParams={search}
            onSearchChange={handleSearchChange}
          />

          <EmployeeProfileTable
            data={rows}
            isLoading={listQuery.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={listQuery.data?.meta.total ?? 0}
            onPaginationChange={handlePage}
            onView={(row) =>
              nav({
                to: "/app/hr/employees/$userId",
                params: { userId: row.userId },
              })
            }
          />
        </div>
      </Main>
    </>
  );
}
